/**
 * v7 — Venda Direta (pedido normal confirmado + NF-e na mesma operação) e pendências de
 * estoque da venda sem estoque. Contratos entre a tela e a API.
 */
import { z } from "zod";
import {
  salePaymentDetailSchema,
  type CalcIssue,
  type FiscalDocumentStatus,
  type FiscalPreview,
} from "./fiscal";
import {
  salesInstallmentSchema,
  salesItemSchema,
  salesOrderNewSchema,
  type PaymentMode,
  type StockShortageStatus,
} from "./vendas";

// ---------------------------------------------------------------- Venda Direta
/**
 * O que a tela envia na prévia e no "Finalizar": os mesmos dados do pedido da v6 (cabeçalho,
 * itens, frete, outras despesas, parcelas), os dados de pagamento da nota e o identificador do
 * rascunho da tela (um pedido por rascunho). Na finalização vai também o hash da prévia
 * conferida.
 */
export const directSaleSchema = salesOrderNewSchema.omit({ loadId: true }).extend({
  clientRequestId: z.uuid("Rascunho da venda inválido: recarregue a tela."),
  items: z
    .array(salesItemSchema)
    .min(1, "Inclua ao menos um item.")
    .max(500, "Máximo de 500 itens"),
  installments: z.array(salesInstallmentSchema).max(120, "Máximo de 120 parcelas").default([]),
  /** dados do pagamento eletrônico que a nota leva (cartão, PIX); vazio = só a forma */
  payments: z.array(salePaymentDetailSchema).max(10).default([]),
  model: z.enum(["55"]).default("55"),
  /** liberações comerciais (as mesmas do Pedido de Venda; o motivo aparece na prévia) */
  releaseDiscount: z.boolean().default(false),
  releaseCredit: z.boolean().default(false),
});
export type DirectSaleInput = z.input<typeof directSaleSchema>;

export const directSaleFinalizeSchema = directSaleSchema.extend({
  previewHash: z.string().regex(/^[0-9a-f]{64}$/, "Faça a prévia fiscal antes de finalizar."),
});
export type DirectSaleFinalizeInput = z.input<typeof directSaleFinalizeSchema>;

/** Falta prevista (ou registrada) de um produto na Venda Direta. */
export type DirectSaleShortage = {
  productId: string;
  productCode: string;
  baseUnitCode: string;
  needed: string;
  available: string;
  short: string;
};

/** Prévia: nada é gravado. `blockers` impedem finalizar; `fiscal` é a prévia da nota. */
export type DirectSalePreview = {
  /** liberações que a venda exige (mesma regra do Pedido de Venda) */
  needsDiscountRelease: boolean;
  needsCreditRelease: boolean;
  /** motivo da liberação comercial (D15): preço fora da tabela / desconto acima do máximo */
  releaseDetails: string[];
  totals: {
    productsTotal: string;
    discountTotal: string;
    freightTotal: string;
    otherTotal: string;
    totalAmount: string;
    paymentMode: PaymentMode | null;
  };
  shortages: DirectSaleShortage[];
  warnings: string[];
  blockers: CalcIssue[];
  fiscal: FiscalPreview | null;
  canFinalize: boolean;
};

/** Resultado do "Finalizar": pedido confirmado e a NF-e na fila. */
export type DirectSaleResult = {
  orderId: string;
  orderNumber: string;
  totalAmount: string;
  documentId: string;
  fiscalStatus: FiscalDocumentStatus;
  fiscalNumber: number;
  fiscalSeries: number;
  shortages: DirectSaleShortage[];
  /** true quando a resposta veio de um "Finalizar" anterior do mesmo rascunho */
  replayed: boolean;
};

// ---------------------------------------------------------------- pendências de estoque
export type StockShortageRegularization = {
  id: string;
  lotId: string | null;
  lotCode: string | null;
  quantity: string;
  status: "confirmado" | "estornado";
  createdAt: string;
  createdByEmail: string | null;
};

export type StockShortageRow = {
  id: string;
  salesOrderId: string;
  orderNumber: string;
  orderOrigin: "pedido" | "venda_direta";
  orderDate: string;
  customerName: string;
  productId: string;
  productCode: string;
  productName: string;
  controlsLot: boolean;
  baseUnitCode: string;
  warehouseId: string;
  warehouseName: string;
  /** falta registrada na venda (unidade-base) */
  quantity: string;
  regularized: string;
  open: string;
  /** retrato na hora da venda */
  sellable: string;
  committed: string;
  status: StockShortageStatus;
  authorizedByEmail: string | null;
  authorizedAt: string;
  closedAt: string | null;
  closedReason: string | null;
  regularizations: StockShortageRegularization[];
};

export const stockShortageQuerySchema = z.object({
  status: z.enum(["aberta", "regularizada", "cancelada", "todas"]).default("aberta"),
  warehouseId: z.preprocess((v) => (v === "" ? undefined : v), z.uuid().optional()),
  q: z.string().trim().max(100).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});

/** Regularizar: lote escolhido (ou sem lote, produto sem lote) ou por FEFO (lotes válidos). */
export const stockShortageRegularizeSchema = z.discriminatedUnion("mode", [
  z.object({
    mode: z.literal("lote"),
    lotId: z.uuid().nullable().default(null),
    quantity: z
      .string()
      .trim()
      .regex(/^\d{1,12}(\.\d{1,6})?$/, "Quantidade inválida")
      .refine((v) => Number(v) > 0, "Informe a quantidade"),
  }),
  z.object({ mode: z.literal("fefo") }),
]);
export type StockShortageRegularizeInput = z.input<typeof stockShortageRegularizeSchema>;

// ---------------------------------------------------------------- acompanhamento fiscal
/** v7: notas que pedem atenção (rejeitada, denegada, incerta, falha de processamento). */
export const FISCAL_ATTENTION_KINDS = [
  "rejeitada",
  "denegada",
  "incerta",
  "falha",
  "na_fila",
] as const;
export type FiscalAttentionKind = (typeof FISCAL_ATTENTION_KINDS)[number];
export const FISCAL_ATTENTION_LABEL: Record<FiscalAttentionKind, string> = {
  rejeitada: "Rejeitada pela SEFAZ",
  denegada: "Denegada",
  incerta: "Sem resposta (consultando)",
  falha: "Falha de processamento",
  na_fila: "Na fila há mais de 10 minutos",
};
export type FiscalAttentionRow = {
  documentId: string;
  kind: FiscalAttentionKind;
  status: FiscalDocumentStatus;
  model: string;
  series: number;
  number: number;
  issueDate: string;
  totalAmount: string;
  recipientName: string | null;
  salesOrderId: string | null;
  orderNumber: string | null;
  orderOrigin: "pedido" | "venda_direta" | null;
  /** último motivo (rejeição, erro do provedor) */
  reason: string | null;
  updatedAt: string;
};
